import React from 'react';
import { Modal } from 'antd';
import { Image, PDFViewer } from '@react-pdf/renderer';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import poppins from '../../../assets/fonts/Poppins/Poppins-Regular.ttf';
import poppinsBold from '../../../assets/fonts/Poppins/Poppins-Bold.ttf';
import poppinsItalic from '../../../assets/fonts/Poppins/Poppins-Italic.ttf';
import poppinsBoldItalic from '../../../assets/fonts/Poppins/Poppins-BoldItalic.ttf';
import logo from '../../../assets/logos/logo-factura.jpg';
import { usuario } from '../../../resources';


Font.register({
  family: "Poppins",
  fontStyle: "normal",
  fontWeight: "normal",
  fonts: [
    { src: poppins },
    { src: poppinsBold, fontWeight: "bold" },
    { src: poppinsItalic, fontStyle: "italic" },
    { src: poppinsBoldItalic, fontStyle: "italic", fontWeight: "bold" },
  ],
});

Font.register({ family: 'PoppinsBold', src: poppinsBold });

// ─── Static option lists ───────────────────────────────────────────────────────

const TRATAMIENTOS_LIST = [
  { key: 'EMT',       label: 'Estimulación Magnética\nTranscraneal (EMT)' },
  { key: 'Ketamina',  label: 'Infusión con Ketamina' },
  { key: 'Esketamina',label: 'Esketamina' },
  { key: 'TEC',       label: 'Terapia Electroconvulsiva\n(TEC)' },
];

const HISTORIAL_LIST = [
  'Epilepsia o crisis convulsivas',
  'Implantes metálicos en la cabeza',
  'Enfermedades pulmonares',
  'Migraña crónica cefalea severa',
  'Hipertensión arterial',
  'Abuso de sustancias',
  'Enfermedad hepática grave',
  'Enfermedad cardiovascular',
  'Ninguna de las anteriores',
];

const TRAT_PREVIOS_LIST = [
  'Antidepresivos',
  'Neuromodulación',
  'Antipsicóticos',
  'Psicoterapia',
  'Ninguna de las anteriores',
];

const MEDICACION_LIST = [
  'Antidepresivos',
  'Bupropión',
  'Benzodiacepinas',
  'Analgésicos opiáceos',
  'Antipsicóticos',
  'Antiepilépticos',
  'Ninguna de las anteriores',
];

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 7.5,
    fontFamily: 'Poppins',
  },
  // Header
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    alignItems: 'center',
  },
  refText: {
    fontSize: 7.5,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  title: {
    fontStyle: 'italic',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 8,
  },
  // Patient info box
  patientBox: {
    backgroundColor: '#f0f0f0',
    border: '0.5pt solid #bbb',
    padding: 5,
    marginBottom: 8,
  },
  patientRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  patientField: {
    flexDirection: 'row',
    marginRight: 12,
  },
  patientLabel: {
    fontWeight: 'bold',
    fontSize: 7.5,
  },
  patientValue: {
    fontSize: 7.5,
    marginLeft: 2,
  },
  // Three-column section
  columnsRow: {
    flexDirection: 'row',
  },
  columnWrapper: {
    flex: 1,
    marginRight: 6,
  },
  columnWrapperLast: {
    flex: 1,
  },
  columnTitle: {
    backgroundColor: '#24a1d6',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 3,
    marginBottom: 4,
    fontSize: 7.5,
  },
  subTitle: {
    backgroundColor: '#24a1d6',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: 3,
    marginTop: 6,
    marginBottom: 4,
    fontSize: 7.5,
  },
  // Check item
  checkRow: {
    flexDirection: 'row',
    marginBottom: 3,
    alignItems: 'flex-start',
  },
  checkBullet: {
    width: 9,
    fontSize: 7,
  },
  checkLabel: {
    flex: 1,
    fontSize: 7,
  },
  checkLabelBold: {
    flex: 1,
    fontSize: 7,
    fontWeight: 'bold',
  },
  sessionsLine: {
    fontSize: 6,
    fontStyle: 'italic',
    marginLeft: 9,
    marginBottom: 2,
    color: '#333',
  },
  // Signature table
  sigTable: {
    border: '0.5pt solid #aaa',
    marginTop: 10,
  },
  sigRow: {
    flexDirection: 'row',
    borderBottom: '0.5pt solid #aaa',
  },
  sigRowLast: {
    flexDirection: 'row',
  },
  sigCellLeft: {
    flex: 1,
    padding: 5,
    borderRight: '0.5pt solid #aaa',
    fontSize: 7,
  },
  sigCellRight: {
    flex: 1,
    padding: 5,
    fontSize: 7,
  },
  sigLabel: {
    fontWeight: 'bold',
    fontSize: 7,
    marginBottom: 2,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 6,
    color: '#666',
  },
  footerPage: {
    position: 'absolute',
    bottom: 20,
    right: 30,
    fontSize: 6,
    color: '#666',
  },
});

// ─── Helper: single checkbox row ───────────────────────────────────────────────

const CheckItem = ({ label, checked }) => (
  <View style={styles.checkRow}>
    <Text style={styles.checkBullet}>{checked ? '●' : '○'}</Text>
    <Text style={checked ? styles.checkLabelBold : styles.checkLabel}>{label}</Text>
  </View>
);

// ─── Detect which treatment key is active from the `tipo` prop ────────────────

const getTipoKey = (tipo) => {
  const t = (tipo || '').toLowerCase();
  if (t.includes('esketamina'))                          return 'Esketamina';
  if (t.includes('ketamina'))                            return 'Ketamina';
  if (t.includes('estimul') || t.includes('transcraneal') || t.includes('emt')) return 'EMT';
  if (t.includes('tec') || t.includes('electroconvuls')) return 'TEC';
  return null;
};

// ─── Main PDF component ────────────────────────────────────────────────────────

// Exported for embedding inside other Documents (e.g. expedienteForPrintNext)
export const SolicitudPage = ({ pacienteData, visitaData, tipo }) => {
  const historial   = Array.isArray(visitaData?.historial_relevante)   ? visitaData.historial_relevante   : [];
  const tratPrevios = Array.isArray(visitaData?.tratamientos_previos)  ? visitaData.tratamientos_previos  : [];
  const medicacion  = Array.isArray(visitaData?.medicacion_actual)     ? visitaData.medicacion_actual      : [];
  const tipoKey     = getTipoKey(tipo);

  return (
    <Page size="A4" style={styles.page}>

        {/* ── Header ── */}
        <View style={styles.headerRow}>
          <Image src={logo} style={{ width: 110 }} />
          <View>
            <Text style={styles.refText}>R-CDL—050 Solicitud de tratamiento</Text>
            <Text style={[styles.refText, { fontWeight: 'normal', marginTop: 2 }]}>Ed 03 12/11/2025</Text>
          </View>
        </View>

        {/* ── Title ── */}
        <Text style={styles.title}>Solicitud de tratamiento</Text>

        {/* ── Patient info ── */}
        <View style={styles.patientBox}>
          <View style={styles.patientRow}>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Nombre del Paciente:</Text>
              <Text style={styles.patientValue}>{pacienteData.name}</Text>
            </View>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Folio de Identificación del Paciente*:</Text>
              <Text style={styles.patientValue}>{pacienteData._id?.slice(-5).toUpperCase()}</Text>
            </View>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Edad:</Text>
              <Text style={styles.patientValue}>{pacienteData.edad}</Text>
            </View>
          </View>
          <View style={styles.patientRow}>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Sexo:</Text>
              <Text style={styles.patientValue}>{pacienteData.sexo}</Text>
            </View>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Diagnóstico:</Text>
              <Text style={styles.patientValue}>{pacienteData.diagnostico}</Text>
            </View>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Correo electrónico:</Text>
              <Text style={styles.patientValue}>{pacienteData.email || ''}</Text>
            </View>
          </View>
          <View style={styles.patientRow}>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Teléfono:</Text>
              <Text style={styles.patientValue}>{pacienteData.telefono || ''}</Text>
            </View>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Nombre Responsable:</Text>
              <Text style={styles.patientValue}>{pacienteData.nombre_responsable || ''}</Text>
            </View>
            <View style={styles.patientField}>
              <Text style={styles.patientLabel}>Teléfono Responsable:</Text>
              <Text style={styles.patientValue}>{pacienteData.telefono_responsable || ''}</Text>
            </View>
          </View>
        </View>

        {/* ── Three-column section ── */}
        <View style={styles.columnsRow}>

          {/* Column 1: Tratamientos solicitados */}
          <View style={styles.columnWrapper}>
            <Text style={styles.columnTitle}>Tratamientos solicitados</Text>
            {TRATAMIENTOS_LIST.map(({ key, label }) => {
              const checked = key === tipoKey;
              return (
                <View key={key}>
                  <CheckItem label={label} checked={checked} />
                  <Text style={styles.sessionsLine}>
                    Sesiones propuestas: {checked ? (visitaData.numero_sesiones ?? '___') : '___'}
                  </Text>
                </View>
              );
            })}
          </View>

          {/* Column 2: Historial médico relevante */}
          <View style={styles.columnWrapper}>
            <Text style={styles.columnTitle}>Historial médico relevante</Text>
            {HISTORIAL_LIST.map((item) => (
              <CheckItem key={item} label={item} checked={historial.includes(item)} />
            ))}
          </View>

          {/* Column 3: Tratamientos previos + Medicación actual */}
          <View style={styles.columnWrapperLast}>
            <Text style={styles.columnTitle}>Tratamientos previos</Text>
            {TRAT_PREVIOS_LIST.map((item) => (
              <CheckItem key={item} label={item} checked={tratPrevios.includes(item)} />
            ))}

            <Text style={styles.subTitle}>Medicación actual</Text>
            {MEDICACION_LIST.map((item) => (
              <CheckItem key={item} label={item} checked={medicacion.includes(item)} />
            ))}
          </View>

        </View>

        {/* ── Signature table ── */}
        <View style={styles.sigTable}>
          <View style={styles.sigRow}>
            <View style={styles.sigCellLeft}>
              <Text style={styles.sigLabel}>Nombre del médico que refiere:</Text>
              <Text>{visitaData.medico_refiere || ''}</Text>
            </View>
            <View style={styles.sigCellRight}>
              <Text style={styles.sigLabel}>Nombre del responsable del tratamiento:</Text>
              <Text>{usuario.name}</Text>
            </View>
          </View>
          <View style={styles.sigRow}>
            <View style={styles.sigCellLeft}>
              <Text>Fecha: {visitaData?.createdAt ? new Date(visitaData.createdAt).toLocaleDateString() : ''}</Text>
            </View>
            <View style={styles.sigCellRight}>
              <Text>Fecha: {new Date().toLocaleDateString()}</Text>
            </View>
          </View>
          <View style={styles.sigRowLast}>
            <View style={styles.sigCellLeft}>
              <Text style={styles.sigLabel}>Nombre del médico responsable:</Text>
              {(pacienteData.medicos_asignados || []).map((m, i) => (
                <Text key={i}>{m?.name}</Text>
              ))}
            </View>
            <View style={styles.sigCellRight}>
              <Text style={styles.sigLabel}>Autorización de ingreso a tratamiento:</Text>
              <Text>Aprobado   [ {visitaData?.aprobado ? '●' : ' '} ]</Text>
              <Text>Rechazado  [ {!visitaData?.aprobado ? '●' : ' '} ]</Text>
              <Text style={{ marginTop: 4 }}>Firma:</Text>
            </View>
          </View>
        </View>

        {/* ── Footer ── */}
        <Text style={styles.footer}>
          Este documento es propiedad de CIDERALT y no puede ser distribuido externamente sin la autorización escrita de los socios propietarios
        </Text>
        <Text style={styles.footerPage}>Página 1 de 1</Text>

      </Page>
  );
};

// Standalone document (used by the modal)
const SolicitudKetaminaPDF = ({ pacienteData, visitaData, tipo }) => (
  <Document>
    <SolicitudPage pacienteData={pacienteData} visitaData={visitaData} tipo={tipo} />
  </Document>
);

// ─── Modal wrapper ─────────────────────────────────────────────────────────────

const SolicitudKetaminaModal = ({ isModalOpen, setIsModalOpen, pacienteData, visitaData, tipo }) => (
  <Modal
    open={isModalOpen}
    onCancel={() => setIsModalOpen(false)}
    footer={null}
    width={900}
    style={{ top: 20 }}
    title="Solicitud de tratamiento"
    destroyOnClose
  >
    <PDFViewer width="100%" height={600}>
      <SolicitudKetaminaPDF pacienteData={pacienteData} visitaData={visitaData} tipo={tipo} />
    </PDFViewer>
  </Modal>
);

export default SolicitudKetaminaModal;
